import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { useUserProfile } from "../contexts/UserProfileContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

// Hilfsfunktion: Bild verkleinern und komprimieren
async function resizeImage(
  file: File,
  maxWidth = 800,
  quality = 0.7
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      if (!e.target?.result) return;
      img.src = e.target.result as string;
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = maxWidth / img.width;
      canvas.width = maxWidth;
      canvas.height = img.height * scale;

      const ctx = canvas.getContext("2d");
      if (!ctx) return reject("Kein Canvas-Kontext verfügbar");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject("Fehler beim Erstellen des komprimierten Bildes");
        },
        "image/jpeg",
        quality
      );
    };

    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

export default function CreateBlogPost() {
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  if (!user || !(profile?.role === "creator" || profile?.role === "admin")) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <p className="text-center text-red-400">
          Du hast keine Berechtigung, Blogposts zu erstellen.
        </p>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    let coverImageUrl = undefined;

    if (coverImage) {
      try {
        // ✨ Bild zuerst verkleinern
        const resizedBlob = await resizeImage(coverImage);

        const { data, error: uploadError } = await supabase.storage
          .from("blog-covers")
          .upload(`cover-${crypto.randomUUID()}.jpg`, resizedBlob);

        if (uploadError) {
          setError("Fehler beim Hochladen des Bildes.");
          return;
        }

        coverImageUrl = data?.path
          ? supabase.storage.from("blog-covers").getPublicUrl(data.path).data
              .publicUrl
          : undefined;
      } catch (resizeError) {
        console.error(resizeError);
        setError("Fehler beim Verkleinern des Bildes.");
        return;
      }
    }

    const { error: insertError } = await supabase.from("blog_posts").insert([
      {
        title,
        content,
        cover_image_url: coverImageUrl,
        creator_id: user.id,
      },
    ]);

    if (insertError) {
      setError("Fehler beim Erstellen des Blogposts.");
    } else {
      setSuccess("✅ Beitrag erfolgreich erstellt!");

      setTimeout(() => {
        navigate("/dashboard"); // 🚀 Erfolgreich zum Dashboard navigieren
      }, 1000);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <div className="max-w-4xl mx-auto pt-6 pb-20 px-4 sm:px-6">
        <h1 className="text-2xl font-bold text-slate-100 mb-6">Neuen Blogbeitrag erstellen</h1>
        <div className="p-4 bg-[#1e2639] border border-[#2a3347] rounded text-sm mb-4">
          <p className="font-semibold mb-2 text-slate-300">🛠 Markdown Tipps:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li>
              <code className="bg-[#0d1117] text-sky-300 px-1 rounded">**fett**</code> → <strong className="text-slate-300">fett</strong>
            </li>
            <li>
              <code className="bg-[#0d1117] text-sky-300 px-1 rounded">*kursiv*</code> → <em className="text-slate-300">kursiv</em>
            </li>
            <li>
              <code className="bg-[#0d1117] text-sky-300 px-1 rounded">~~durchgestrichen~~</code> → <s className="text-slate-300">durchgestrichen</s>
            </li>
            <li>
              <code className="bg-[#0d1117] text-sky-300 px-1 rounded">`Code`</code> → <code className="bg-[#0d1117] text-sky-300 px-1 rounded">Code</code>
            </li>
            <li>
              <code className="bg-[#0d1117] text-sky-300 px-1 rounded">[Linktext](https://example.com)</code> → Link
            </li>
            <li>Tabellen, Checklisten und vieles mehr wird unterstützt!</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Titel"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
            required
          />
          <div className="flex flex-col md:flex-row gap-6">
            {/* Eingabe */}
            <div className="flex-1">
              <label className="block mb-1 text-sm text-slate-400 font-medium">Inhalt (Markdown)</label>
              <textarea
                placeholder="Schreibe hier deinen Inhalt in Markdown..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded h-72 resize-none outline-none focus:border-sky-400 placeholder:text-slate-600"
                required
              />
            </div>

            {/* Live-Preview */}
            <div className="flex-1 border border-[#2a3347] rounded p-2 bg-[#161b27] overflow-y-auto h-72">
              <label className="block mb-1 text-sm text-slate-400 font-medium">Vorschau</label>
              <div className="prose prose-lg prose-invert max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                >
                  {content || "*Hier erscheint deine Vorschau...*"}
                </ReactMarkdown>
              </div>
            </div>
          </div>

          <input
            type="file"
            accept="image/*"
            onChange={(e) => setCoverImage(e.target.files?.[0] || null)}
            className="w-full text-slate-400"
          />
          <button
            type="submit"
            className="bg-sky-500 text-white py-2 px-4 rounded hover:bg-sky-600 transition-colors w-full"
          >
            Beitrag veröffentlichen
          </button>
        </form>

        {error && <p className="text-red-400 mt-4">{error}</p>}
        {success && <p className="text-emerald-400 mt-4">{success}</p>}
      </div>
    </div>
  );
}
